import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Library, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createCollection } from '@/lib/api/collections/get-collections'
import { apiErrorMessage } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { Collection } from '@/types/terms'

interface CollectionPickerProps {
  collections: Collection[]
  value: number | null
  onChange: (id: number) => void
}

/**
 * Which term base is being browsed (or committed into). Every document, term
 * and concept belongs to exactly one collection, so this is the outermost scope
 * — nothing below it crosses collection boundaries.
 *
 * Creating happens here rather than on a separate admin page: you usually
 * discover you need a new collection at the moment you are trying to pick one.
 */
export function CollectionPicker({ collections, value, onChange }: CollectionPickerProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const active = collections.find((c) => c.id === value)

  const close = () => {
    setOpen(false)
    setCreating(false)
    setName('')
  }

  const create = useMutation({
    mutationFn: () => createCollection(name.trim()),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['collections'] })
      onChange(res.id) // jump straight into what was just created
      toast.success(t('collections.created', { name: res.name }))
      close()
    },
    onError: (e) =>
      toast.error(t('collections.createFailed'), { description: apiErrorMessage(e) }),
  })

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => (open ? close() : setOpen(true))}
        className="max-w-56 justify-between gap-2"
      >
        <Library className="shrink-0 text-muted-foreground" />
        <span className="truncate">{active?.name ?? t('collections.picker')}</span>
        <ChevronsUpDown className="shrink-0 text-muted-foreground" />
      </Button>

      {open && (
        <>
          {/* Click-away layer: cheaper than wiring a popover for one control. */}
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute left-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-md border border-border bg-card shadow-lg">
            <div className="max-h-72 overflow-y-auto scrollbar-thin">
              {collections.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.id)
                    close()
                  }}
                  className={cn(
                    'flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left transition-colors',
                    c.id === value ? 'bg-accent' : 'hover:bg-muted',
                  )}
                >
                  <Check
                    className={cn(
                      'mt-0.5 size-3.5 shrink-0',
                      c.id === value ? 'text-primary' : 'invisible',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {t('collections.counts', {
                        terms: t('units.terms', { count: c.term_count }),
                        concepts: t('units.concepts', { count: c.concept_count }),
                        documents: t('units.documents', { count: c.document_count }),
                      })}
                    </p>
                  </div>
                  {c.my_role && c.my_role !== 'owner' && (
                    <span className="mt-0.5 shrink-0 text-[10px] text-muted-foreground">
                      {t(`roles.${c.my_role}` as const)}
                    </span>
                  )}
                </button>
              ))}
              {!collections.length && (
                <p className="p-3 text-xs text-muted-foreground">{t('collections.empty')}</p>
              )}
            </div>

            {creating ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (name.trim()) create.mutate()
                }}
                className="flex items-center gap-1.5 border-t border-border bg-muted/40 p-2"
              >
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setCreating(false)}
                  placeholder={t('collections.newName')}
                  className="h-7 flex-1 text-xs"
                  disabled={create.isPending}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!name.trim() || create.isPending}
                >
                  {create.isPending ? <Loader2 className="animate-spin" /> : t('collections.create')}
                </Button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-muted"
              >
                <Plus className="size-3.5" />
                {t('collections.new')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
